//@ts-ignore
import { SUBJECTS_SLUGS } from '@yandex-int/k-common/constants';
import b from 'b_';
import { Checkmark, WarningFilled } from 'education-icons';
import { SCREEN_RESOLUTIONS } from 'platform-components/constants';
import {
    withRouterPath,
    InjectedProps as WithRouterPathProps,
} from 'platform-components/src/utils/hocs/with-router-path/with-router-path';
import React from 'react';
import { connect } from 'react-redux';
import ym from 'react-yandex-metrika';
import { Action, bindActionCreators, Dispatch } from 'redux';
import { accessibleClick } from '@edu-frontend/modules/src/shared/lib/a11y';

import { ProblemWithLabView } from 'common.components/lab-problem/lab-problem';
import * as problemViewSelectors from 'common.components/lesson-view.problem-view/selectors';
import * as lessonViewActions from 'common.components/lesson-view.store/actions';
import * as lessonViewSelectors from 'common.components/lesson-view.store/selectors';
import * as lessonSelectors from 'common.components/lesson/selectors';
import { CARD_BADGES } from 'common.components/stack/constants';
import { getCourseByClassAndId } from 'store/courses/selectors';
import * as problemsViewSelectors from 'store/problems-view/selectors';
import * as problemSelectors from 'store/problems/selectors';
import { ProblemInCourse } from 'store/problems/typings';
import { getTeamIdFromPath } from 'utils/path';

import './problem-view__variants.scss';

interface StoreProps {
    cardBadges: Record<number, CARD_BADGES>;
    hasVariants: boolean;
    problems: Array<ProblemInCourse>;
    selectedProblemId: number | null;
    stackId: number | null;
    subject: SUBJECTS_SLUGS | undefined;
}

interface DispatchProps {
    changeProblemVariant: typeof lessonViewActions.changeProblemVariant;
}

interface Props extends StoreProps, DispatchProps, WithRouterPathProps {}

export const ProblemViewVariants = (props: Props) => {
    const { cardBadges, changeProblemVariant, hasVariants, problems, selectedProblemId, stackId, subject } = props;

    const handleChangeVariant = (newProblemId: number) => {
        if (selectedProblemId) {
            changeProblemVariant(selectedProblemId, newProblemId);

            ym('reachGoal', 'teacher_lesson_creation_card_another_option', {
                subject,
                themeId: stackId,
                from_problem_id: selectedProblemId,
                to_problem_id: newProblemId,
            });
        }
    };

    if (!hasVariants) {
        return null;
    }

    return (
        <div className="problem-view__variants">
            <div className="problem-view__variants-list">
                {problems.map(({ problem }) => {
                    const id = problem?.id;
                    const selected = id === selectedProblemId;

                    if (!id) {
                        return null;
                    }

                    const shouldShowUsedInCourseBadge = cardBadges[id] === CARD_BADGES.USED_IN_COURSE;
                    const shouldShowUsedInLessonBadge = cardBadges[id] === CARD_BADGES.USED_IN_LESSON;

                    const disabled = shouldShowUsedInLessonBadge && !selected;

                    return (
                        <div
                            className={b('problem-view', 'problem-variant', {
                                disabled,
                                selected,
                                badge: cardBadges[id],
                            })}
                            key={id}
                            {...accessibleClick(disabled ? undefined : () => handleChangeVariant(id))}
                        >
                            {shouldShowUsedInCourseBadge && (
                                <WarningFilled className={b('problem-view', 'badge', { 'in-course': true })} />
                            )}
                            {shouldShowUsedInLessonBadge && (
                                <Checkmark className={b('problem-view', 'badge', { 'in-lesson': true })} />
                            )}
                            <ProblemWithLabView
                                imageResolution={SCREEN_RESOLUTIONS.X1}
                                isFilled
                                minWidth={960}
                                problem={problem!}
                            />
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

const mapStateToProps = (storeState: any): StoreProps => {
    const stackId = lessonViewSelectors.getProblemStackId(storeState);
    const courseId = lessonSelectors.getLessonOrCurrentYearCourseId(storeState);
    const classId = getTeamIdFromPath();
    const course = getCourseByClassAndId(storeState, { classId, courseId });

    return {
        cardBadges: lessonSelectors.getCardBadges(storeState),
        hasVariants: problemViewSelectors.getHasVariants(storeState, { stackId }),
        selectedProblemId: problemsViewSelectors.getSelectedProblemId(storeState),
        problems: problemSelectors.getProblemsWithUsage(storeState, { stackId }),
        stackId,
        subject: course?.subject,
    };
};

const mapDispatchToProps = (dispatch: Dispatch<Action>): DispatchProps => {
    return bindActionCreators(
        {
            changeProblemVariant: lessonViewActions.changeProblemVariant,
        },
        dispatch
    );
};

export default withRouterPath(connect(mapStateToProps, mapDispatchToProps)(ProblemViewVariants));
